import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from '../../services/workflow/workflow.service';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { PayeeWorkflowProvidedMediaComponent } from '../../payee-workflow-provided-media/payee-workflow-provided-media.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDropzoneComponent } from '../../payee-dropzone/payee-dropzone.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';

type DialogData = {
  transition_label: string;
  original_message: string;
  submit_label: string;
  original_request_label: string;
  response_label: string;
  message_required: boolean;
};

@Component({
  selector: 'app-custom-message-response-dialog',
  templateUrl: './custom-message-response-dialog.component.html',
  styleUrls: ['./custom-message-response-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeWorkflowProvidedMediaComponent,
    PayeeFormFieldComponent,
    PayeeDropzoneComponent,
    PayeeIconComponent,
    PayeeIconButtonComponent,
    PayeeButtonComponent,
    TranslateModule,
  ],
})
export class CustomMessageResponseDialogComponent implements OnInit {
  readonly data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<MatDialogRef<CustomMessageResponseDialogComponent>>(MatDialogRef);
  private workflowService = inject(WorkflowService);
  private fb = inject(FormBuilder);

  loading = false;
  dialogData?: DialogData;
  messageRequired = true;

  readonly form = this.fb.group({
    message: this.fb.control('', {
      nonNullable: true,
      validators: [Validators.maxLength(10000)],
    }),
    files: this.fb.control<File[]>([], { nonNullable: true }),
  });

  get files() {
    return this.form.controls.files;
  }

  async ngOnInit() {
    this.dialogData = (await this.workflowService.getDialogData({
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
    })) as DialogData;

    this.messageRequired = this.dialogData.message_required ?? true;
    this.configureValidators();
  }

  private configureValidators(): void {
    const messageControl = this.form.controls.message;

    if (this.messageRequired) {
      messageControl.setValidators([
        Validators.required,
        Validators.maxLength(10000),
      ]);
    } else {
      messageControl.setValidators([Validators.maxLength(10000)]);
    }

    messageControl.updateValueAndValidity();
  }

  onDrop(files: FileList) {
    const fileValues = [...this.files.value];
    for (let i = 0; i < files.length; ++i) {
      fileValues.push(files.item(i) as File);
    }
    this.files.setValue(fileValues);
  }

  removeFile(i: number) {
    const fileValues = [...this.files.value];
    fileValues.splice(i, 1);
    this.files.setValue(fileValues);
  }

  readonly onSubmit = async () => {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const { message, files } = this.form.getRawValue();
    const trimmedMessage = message.trim();

    const { caseId, transitionId, extraParams, media } = this.data;
    const closeValue: RunTransition = {
      caseId,
      transitionId,
      params: {
        ...extraParams,
        message: trimmedMessage || null,
        files: [...(media?.map((m) => m.uuid) ?? []), ...files],
      },
    };

    this.loading = true;
    try {
      await this.workflowService.runTransition(closeValue);
      this.dialogRef.close(closeValue);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading = false;
    }
  };

  readonly onCancel = () => {
    this.dialogRef.close();
  };
}
